import React from 'react';
import { useTheme, Theme } from '../contexts/ThemeContext';
import { useBrandingContext } from '../contexts/BrandingContext';
import { CompactThemeSwitcher } from './ThemeGallery';
import { LocaleSelectorCompact } from './LocaleSelector';
import './Footer.css';

const Footer: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const { config: branding } = useBrandingContext();
  const [themeBranding, setThemeBranding] = React.useState<any>(null);
  
  // Listen for theme branding changes
  React.useEffect(() => {
    const handleThemeBranding = (e: any) => {
      // e.detail is null when reverting to defaults
      setThemeBranding(e.detail || null);
    };
    
    window.addEventListener('theme-branding-changed', handleThemeBranding);
    
    // Check if theme branding already set
    if ((window as any).__THEME_BRANDING__) {
      setThemeBranding((window as any).__THEME_BRANDING__);
    }
    
    return () => window.removeEventListener('theme-branding-changed', handleThemeBranding);
  }, []);
  
  // Merge theme branding with default branding
  const footerText = themeBranding?.footer?.text || branding?.footer?.text || '© 2026 EmpowerID. All rights reserved.';
  const footerLinks = themeBranding?.footer?.links || branding?.footer?.links || [];
  const footerBadges = themeBranding?.footer?.badges || [];

  return (
    <footer className="app-footer" data-testid="experience-footer">
      <div className="footer-content">
        <div className="footer-left">
          <span className="footer-text">
            {footerText}
          </span>
          
          {footerBadges.length > 0 && (
            <div className="footer-badges" data-testid="experience-footer-badges">
              {footerBadges.map((badge: { src: string; alt: string }, idx: number) => (
                <img
                  key={idx}
                  src={badge.src}
                  alt={badge.alt}
                  height={28}
                  loading="lazy"
                  style={{ display: 'block', opacity: 0.92 }}
                />
              ))}
            </div>
          )}
          
          {/* Footer links if configured */}
          {footerLinks && footerLinks.length > 0 && (
            <div style={{ marginLeft: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
              {footerLinks.map((link: any, idx: number) => (
                <a
                  key={idx}
                  href={link.url}
                  target={link.url.startsWith('http') ? '_blank' : undefined}
                  rel={link.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                  style={{
                    color: 'inherit',
                    fontSize: 14,
                    textDecoration: 'none',
                    opacity: 0.8,
                    transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.8')}
                >
                  {link.label}
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="footer-right" data-testid="experience-footer-actions">
          {/* Locale selector - i18n foundation v1.4.0 */}
          <LocaleSelectorCompact tone="on-dark" />
          
          {/* Theme switcher - now authorization-protected! */}
          <CompactThemeSwitcher showLabel={false} />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
